import { create } from 'zustand';
import { ConversationMessage } from '@/context/explorer';
import { ChainOfThoughtEvent } from '@/components/ai-elements/chain-of-thought-tracker';

interface CodeGenerationState {
  // Core conversation state
  messages: ConversationMessage[];
  isGenerating: boolean;
  statusMessage: string;
  userPrompt: string;
  currentSessionId: string | null;

  // AI response state
  aiResponse: string;
  aiResponseOperationIds: string[];

  // Chain of thought tracking - per message
  messageEvents: Map<string, ChainOfThoughtEvent[]>;
  currentMessageId: string | null;

  // Incremented whenever conversation is externally reset/cleared.
  // Used by hooks to invalidate in-flight generation streams.
  conversationVersion: number;

  // Navigation control
  isNavigatingHome: boolean;

  // Follow-up input state
  isTypingFollowUp: boolean;

  // Actions
  setMessages: (messages: ConversationMessage[]) => void;
  setIsGenerating: (generating: boolean) => void;
  setStatusMessage: (message: string) => void;
  setUserPrompt: (prompt: string) => void;
  setCurrentSessionId: (sessionId: string | null) => void;
  setAiResponse: (response: string) => void;
  setAiResponseOperationIds: (operationIds: string[]) => void;
  addChainOfThoughtEvent: (
    messageId: string,
    event: Omit<ChainOfThoughtEvent, 'timestamp' | 'message_id'>
  ) => void;
  getEventsForMessage: (messageId: string) => ChainOfThoughtEvent[];
  clearEventsForMessage: (messageId: string) => void;
  setCurrentMessageId: (messageId: string | null) => void;
  setIsNavigatingHome: (navigating: boolean) => void;
  setIsTypingFollowUp: (typing: boolean) => void;

  // Composite actions
  clearConversation: () => void;
  resetToHomepage: () => void;
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string) => void;
  updateLatestAssistantMessage: (content: string) => void;
}

export const useCodeGenerationStore = create<CodeGenerationState>((set) => ({
  // Initial state
  messages: [],
  isGenerating: false,
  statusMessage: '',
  userPrompt: '',
  currentSessionId: null,
  aiResponse: '',
  aiResponseOperationIds: [],
  messageEvents: new Map(),
  currentMessageId: null,
  conversationVersion: 0,
  isNavigatingHome: false,
  isTypingFollowUp: false,

  // Basic setters
  setMessages: (messages) => set({ messages }),
  setIsGenerating: (isGenerating) => set({ isGenerating }),
  setStatusMessage: (statusMessage) => set({ statusMessage }),
  setUserPrompt: (userPrompt) => set({ userPrompt }),
  setCurrentSessionId: (currentSessionId) => set({ currentSessionId }),
  setAiResponse: (aiResponse) => set({ aiResponse }),
  setAiResponseOperationIds: (aiResponseOperationIds) =>
    set({ aiResponseOperationIds }),

  // Chain of thought actions
  addChainOfThoughtEvent: (messageId, event) =>
    set((state) => {
      const newMessageEvents = new Map(state.messageEvents);
      const existingEvents = newMessageEvents.get(messageId) || [];
      newMessageEvents.set(messageId, [
        ...existingEvents,
        { ...event, timestamp: new Date(), message_id: messageId },
      ]);
      return { messageEvents: newMessageEvents };
    }),
  getEventsForMessage: (messageId) => {
    const { messageEvents } = useCodeGenerationStore.getState();
    return messageEvents.get(messageId) || [];
  },
  clearEventsForMessage: (messageId) =>
    set((state) => {
      const newMessageEvents = new Map(state.messageEvents);
      newMessageEvents.delete(messageId);
      return { messageEvents: newMessageEvents };
    }),
  setCurrentMessageId: (messageId) => set({ currentMessageId: messageId }),
  setIsNavigatingHome: (isNavigatingHome) => set({ isNavigatingHome }),
  setIsTypingFollowUp: (isTypingFollowUp) => set({ isTypingFollowUp }),

  // Composite actions
  clearConversation: () =>
    set((state) => ({
      messages: [],
      isGenerating: false,
      statusMessage: '',
      aiResponse: '',
      aiResponseOperationIds: [],
      currentSessionId: null,
      messageEvents: new Map(),
      currentMessageId: null,
      conversationVersion: state.conversationVersion + 1,
    })),

  resetToHomepage: () =>
    set((state) => ({
      // Reset all AI state
      messages: [],
      isGenerating: false,
      statusMessage: '',
      userPrompt: '',
      currentSessionId: null,
      aiResponse: '',
      aiResponseOperationIds: [],
      messageEvents: new Map(),
      currentMessageId: null,
      conversationVersion: state.conversationVersion + 1,
      isNavigatingHome: true,
    })),

  addUserMessage: (content) => {
    const userMessage: ConversationMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
    }));
  },

  addAssistantMessage: (content) => {
    const assistantMessage: ConversationMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content,
      timestamp: new Date(),
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
    }));
  },

  updateLatestAssistantMessage: (content) => {
    set((state) => {
      const messages = [...state.messages];
      let lastAssistantIndex = -1;

      // Find the last assistant message
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          lastAssistantIndex = i;
          break;
        }
      }

      if (lastAssistantIndex !== -1) {
        messages[lastAssistantIndex] = {
          ...messages[lastAssistantIndex],
          content,
        };
      }

      return { messages };
    });
  },
}));
